import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useId } from '../../common/hooks/useId';
import { createDisplayName } from '@withjoy/joykit/utils';
import { getOverrides } from '@shared/utils/overrides';
import { Search } from '@withjoy/joykit/icons';
import { Modal } from '../Modal';
import { defaultFilter } from './commandScore';
import type {
  CommandProps,
  CommandDialogProps,
  CommandInputProps,
  CommandListProps,
  CommandEmptyProps,
  CommandGroupProps,
  CommandItemProps,
  CommandSeparatorProps,
  CommandShortcutProps,
  CommandContextValue,
  CommandGroupContextValue,
  CommandItemState,
  CommandItemSelectCallback,
  CommandOverrides,
} from './Command.types';
import {
  StyledCommandRoot,
  StyledCommandInputWrapper,
  StyledCommandInputIcon,
  StyledCommandInput,
  StyledCommandList,
  StyledCommandEmpty,
  StyledCommandGroup,
  StyledCommandGroupHeading,
  StyledCommandItem,
  StyledCommandSeparator,
  StyledCommandShortcut,
  commandRootStyles,
  commandInputWrapperStyles,
  commandInputIconStyles,
  commandInputStyles,
  commandListStyles,
  commandEmptyStyles,
  commandGroupStyles,
  commandGroupHeadingStyles,
  commandItemStyles,
  commandItemSelectedStyles,
  commandItemDisabledStyles,
  commandSeparatorStyles,
  commandShortcutStyles,
  commandDialogRootStyles,
} from './Command.styles.internal';

// ============================================================================
// Context
// ============================================================================

const CommandContext = React.createContext<CommandContextValue | null>(null);
const CommandGroupContext = React.createContext<CommandGroupContextValue | null>(null);

function useCommandContext(): CommandContextValue {
  const context = React.useContext(CommandContext);
  if (!context) {
    throw new Error('Command compound components must be used within a Command component');
  }
  return context;
}

function useCommandGroupContext(): CommandGroupContextValue | null {
  return React.useContext(CommandGroupContext);
}

// ============================================================================
// Utility: Extract text content from React children
// ============================================================================

/** Generate a stable accessible ID for a command item from its unique item ID */
function getCommandItemAccessibleId(itemId: string): string {
  return `command-item-${itemId}`;
}

function getTextContent(element: React.ReactNode): string {
  if (typeof element === 'string') {
    return element;
  }
  if (typeof element === 'number') {
    return String(element);
  }
  if (React.isValidElement(element)) {
    const children = element.props.children;
    if (children) {
      return getTextContent(children);
    }
  }
  if (Array.isArray(element)) {
    return element.map(getTextContent).join('');
  }
  return '';
}

// Stable default so the context useMemo doesn't re-create on every render when
// the consumer omits overrides.
const EMPTY_OVERRIDES: CommandOverrides = {};

// ============================================================================
// Custom Hook: useCommandState
// ============================================================================

function useCommandState(props: CommandProps, overrides: CommandOverrides = EMPTY_OVERRIDES) {
  const { shouldFilter = true, filter = defaultFilter, loop = false, value: controlledValue, onValueChange } = props;

  // Generate IDs for accessibility
  const listId = useId(undefined, 'command-list');
  const inputId = useId(undefined, 'command-input');
  const labelId = useId(undefined, 'command-label');

  // Internal state
  const [search, setSearchState] = useState('');
  const [selectedValue, setSelectedValueState] = useState<string | undefined>(controlledValue);

  // Item and group registrations
  const itemsRef = useRef<Map<string, CommandItemState>>(new Map());
  const groupsRef = useRef<Map<string, string>>(new Map());
  const filteredItemsRef = useRef<Map<string, number>>(new Map());
  const itemCallbacksRef = useRef<Map<string, CommandItemSelectCallback>>(new Map());

  // Store current search/filter in refs so callbacks don't need them as dependencies
  const searchRef = useRef(search);
  const shouldFilterRef = useRef(shouldFilter);
  const filterRef = useRef(filter);

  // Keep refs in sync
  searchRef.current = search;
  shouldFilterRef.current = shouldFilter;
  filterRef.current = filter;

  /** Whether filtering is currently inactive (no search or filtering disabled) */
  const isFilterInactive = (): boolean => !searchRef.current || !shouldFilterRef.current;

  // Force re-render when items change
  const [, forceUpdate] = useState({});
  const scheduleUpdate = useCallback(() => forceUpdate({}), []);

  // Update filtered items - reads from refs so it's stable
  const updateFilters = useCallback(() => {
    const currentSearch = searchRef.current;
    const currentShouldFilter = shouldFilterRef.current;
    const currentFilter = filterRef.current;

    // Fast path: when not filtering or no search, just clear the filtered map
    // The getSortedVisibleItems function handles this case directly from itemsRef
    if (!currentShouldFilter || !currentSearch) {
      filteredItemsRef.current.clear();
      return;
    }

    // When searching: calculate scores for each item
    filteredItemsRef.current.clear();
    itemsRef.current.forEach((item, id) => {
      const score = currentFilter(item.value, currentSearch, item.keywords);
      if (score > 0) {
        filteredItemsRef.current.set(id, score);
      }
    });
  }, []); // Empty deps - reads from refs

  // Synchronously update filters during render so child components see correct
  // visibility state immediately. This MUST happen during render, not in a useEffect,
  // to prevent items from unmounting during the render-to-effect gap. Without this,
  // when search changes: searchRef updates (render) → items see empty filteredItemsRef
  // → items unmount & unregister → useEffect runs updateFilters on empty itemsRef → permanent empty state.
  updateFilters();

  // Sync controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setSelectedValueState(controlledValue);
    }
  }, [controlledValue]);

  // Track previous search to detect transitions
  const prevSearchRef = useRef(search);

  // Get sorted visible items (by score descending)
  // Optimized: skip sorting when search is empty (all scores are 1)
  const getSortedVisibleItems = useCallback((): CommandItemState[] => {
    // Fast path: when search is empty or filtering is disabled, all items are visible with score 1
    // No need to sort since all scores are equal - just filter non-disabled items
    if (isFilterInactive()) {
      const items: CommandItemState[] = [];
      itemsRef.current.forEach(item => {
        if (!item.disabled) {
          items.push(item);
        }
      });
      return items;
    }

    // When searching: collect items with their scores and sort
    const items: Array<{ item: CommandItemState; score: number }> = [];

    itemsRef.current.forEach((item, id) => {
      const score = filteredItemsRef.current.get(id);
      if (score !== undefined && score > 0 && !item.disabled) {
        items.push({ item, score });
      }
    });

    // Sort by score descending
    items.sort((a, b) => b.score - a.score);

    return items.map(({ item }) => item);
  }, []);

  // Select first visible item if current selection is not visible
  const ensureValidSelection = useCallback(() => {
    const currentSearch = searchRef.current;
    const previousSearch = prevSearchRef.current;

    // Fast path: when clearing search (going from non-empty to empty),
    // all items become visible, so current selection is likely still valid
    if (!currentSearch && previousSearch) {
      // Just verify current selection is not disabled
      if (selectedValue) {
        const selectedItem = Array.from(itemsRef.current.values()).find(item => item.value === selectedValue);
        if (selectedItem && !selectedItem.disabled) {
          // Selection is still valid, no change needed
          return;
        }
      }
      // Current selection invalid, select first non-disabled item
      const allItems = Array.from(itemsRef.current.values());
      for (let i = 0; i < allItems.length; i++) {
        if (!allItems[i].disabled) {
          setSelectedValueState(allItems[i].value);
          onValueChange?.(allItems[i].value);
          return;
        }
      }
      setSelectedValueState(undefined);
      return;
    }

    // Standard path: when searching, use sorted visible items
    const visibleItems = getSortedVisibleItems();

    if (visibleItems.length === 0) {
      setSelectedValueState(undefined);
      return;
    }

    // Check if current selection is still visible
    const currentVisible = visibleItems.some(item => item.value === selectedValue);
    if (!currentVisible) {
      const firstItem = visibleItems[0];
      setSelectedValueState(firstItem?.value);
      onValueChange?.(firstItem?.value ?? '');
    }
  }, [getSortedVisibleItems, selectedValue, onValueChange]);

  // Ensure valid selection when items change or filters update
  useEffect(() => {
    ensureValidSelection();
    // Update previous search ref after validation
    prevSearchRef.current = search;
  }, [search, ensureValidSelection]);

  // Navigation helpers
  const navigateItems = useCallback(
    (direction: 1 | -1) => {
      const visibleItems = getSortedVisibleItems();
      if (visibleItems.length === 0) return;

      const currentIndex = visibleItems.findIndex(item => item.value === selectedValue);
      let nextIndex: number;

      if (currentIndex === -1) {
        // No selection, select first or last based on direction
        nextIndex = direction === 1 ? 0 : visibleItems.length - 1;
      } else {
        nextIndex = currentIndex + direction;

        if (loop) {
          // Wrap around
          if (nextIndex < 0) nextIndex = visibleItems.length - 1;
          if (nextIndex >= visibleItems.length) nextIndex = 0;
        } else {
          // Clamp
          nextIndex = Math.max(0, Math.min(visibleItems.length - 1, nextIndex));
        }
      }

      const nextItem = visibleItems[nextIndex];
      if (nextItem) {
        setSelectedValueState(nextItem.value);
        onValueChange?.(nextItem.value);
      }
    },
    [getSortedVisibleItems, selectedValue, loop, onValueChange],
  );

  const selectFirst = useCallback(() => {
    const visibleItems = getSortedVisibleItems();
    const firstItem = visibleItems[0];
    if (firstItem) {
      setSelectedValueState(firstItem.value);
      onValueChange?.(firstItem.value);
    }
  }, [getSortedVisibleItems, onValueChange]);

  const selectLast = useCallback(() => {
    const visibleItems = getSortedVisibleItems();
    const lastItem = visibleItems[visibleItems.length - 1];
    if (lastItem) {
      setSelectedValueState(lastItem.value);
      onValueChange?.(lastItem.value);
    }
  }, [getSortedVisibleItems, onValueChange]);

  // Stable context functions that don't change reference
  // These are stored in refs to prevent context value recreation
  const stableFunctionsRef = useRef({
    registerItem: (id: string, value: string, keywords: ReadonlyArray<string>, disabled: boolean, groupId?: string) => {
      itemsRef.current.set(id, { id, value, keywords, disabled, groupId });
      updateFilters();
      scheduleUpdate();
    },

    unregisterItem: (id: string) => {
      itemsRef.current.delete(id);
      filteredItemsRef.current.delete(id);
      scheduleUpdate();
    },

    updateItem: (id: string, value: string, keywords: ReadonlyArray<string>, disabled: boolean) => {
      const existing = itemsRef.current.get(id);
      if (existing) {
        // Only update if something actually changed
        const keywordsChanged = existing.keywords.length !== keywords.length || existing.keywords.some((kw, i) => kw !== keywords[i]);
        if (existing.value !== value || keywordsChanged || existing.disabled !== disabled) {
          itemsRef.current.set(id, { ...existing, value, keywords, disabled });
          updateFilters();
          scheduleUpdate();
        }
      }
    },

    registerGroup: (id: string, value: string) => {
      groupsRef.current.set(id, value);
    },

    unregisterGroup: (id: string) => {
      groupsRef.current.delete(id);
    },

    getScore: (id: string) => {
      // When search is empty or filtering is disabled, all items have score 1
      if (isFilterInactive()) {
        return itemsRef.current.has(id) ? 1 : 0;
      }
      return filteredItemsRef.current.get(id) ?? 0;
    },

    getFilteredCount: () => {
      // When search is empty or filtering is disabled, all items are visible
      if (isFilterInactive()) {
        return itemsRef.current.size;
      }
      let count = 0;
      filteredItemsRef.current.forEach(score => {
        if (score > 0) count++;
      });
      return count;
    },

    isItemVisible: (id: string) => {
      // When search is empty or filtering is disabled, all items are visible
      if (isFilterInactive()) {
        return itemsRef.current.has(id);
      }
      const score = filteredItemsRef.current.get(id);
      return score !== undefined && score > 0;
    },

    isGroupVisible: (groupId: string) => {
      // When search is empty or filtering is disabled, all groups are visible
      if (isFilterInactive()) {
        return true;
      }
      // A group is visible if any of its items are visible
      // Use Array.from instead of for-of to avoid transpilation issues with Map iterators (target: es5)
      const entries = Array.from(itemsRef.current.entries());
      for (let i = 0; i < entries.length; i++) {
        const [id, item] = entries[i];
        if (item.groupId === groupId && (filteredItemsRef.current.get(id) ?? 0) > 0) {
          return true;
        }
      }
      return false;
    },

    registerItemCallback: (value: string, callback: CommandItemSelectCallback) => {
      itemCallbacksRef.current.set(value, callback);
    },

    unregisterItemCallback: (value: string) => {
      itemCallbacksRef.current.delete(value);
    },

    triggerItemSelect: (value: string) => {
      const callback = itemCallbacksRef.current.get(value);
      if (callback) {
        callback(value);
      }
    },

    getItemAccessibleId: (value: string): string | undefined => {
      // Use Array.from instead of for-of to avoid potential transpilation issues with Map iterators
      const items = Array.from(itemsRef.current.values());
      for (let i = 0; i < items.length; i++) {
        if (items[i].value === value) {
          return getCommandItemAccessibleId(items[i].id);
        }
      }
      return undefined;
    },
  });

  // Context value - only recreate when actual values change, not functions
  const contextValue: CommandContextValue = useMemo(
    () => ({
      search,
      selectedValue,

      // Stable functions from ref
      registerItem: stableFunctionsRef.current.registerItem,
      unregisterItem: stableFunctionsRef.current.unregisterItem,
      updateItem: stableFunctionsRef.current.updateItem,
      registerGroup: stableFunctionsRef.current.registerGroup,
      unregisterGroup: stableFunctionsRef.current.unregisterGroup,
      getScore: stableFunctionsRef.current.getScore,
      getFilteredCount: stableFunctionsRef.current.getFilteredCount,
      isItemVisible: stableFunctionsRef.current.isItemVisible,
      isGroupVisible: stableFunctionsRef.current.isGroupVisible,
      registerItemCallback: stableFunctionsRef.current.registerItemCallback,
      unregisterItemCallback: stableFunctionsRef.current.unregisterItemCallback,
      triggerItemSelect: stableFunctionsRef.current.triggerItemSelect,
      getItemAccessibleId: stableFunctionsRef.current.getItemAccessibleId,

      setSelectedValue: (value: string | undefined) => {
        setSelectedValueState(value);
        if (value !== undefined) {
          onValueChange?.(value);
        }
      },

      selectItem: (value: string) => {
        const item = Array.from(itemsRef.current.values()).find(i => i.value === value);
        if (item && !item.disabled) {
          setSelectedValueState(value);
          onValueChange?.(value);
        }
      },

      setSearch: (newSearch: string) => {
        setSearchState(newSearch);
      },

      shouldFilter,
      loop,
      listId,
      inputId,
      labelId,
      overrides,
    }),
    [search, selectedValue, shouldFilter, loop, listId, inputId, labelId, onValueChange, overrides],
  );

  return {
    contextValue,
    navigateItems,
    selectFirst,
    selectLast,
    getSortedVisibleItems,
  };
}

// ============================================================================
// Command Root Component
// ============================================================================

/**
 * Command is a command palette component that provides fuzzy search and keyboard navigation.
 * React 17 compatible - does not use useSyncExternalStore.
 *
 * ## Bundle Optimization - IMPORTANT
 *
 * This component should be lazy loaded using `loadable()` to avoid
 * pulling it into the initial bundle.
 *
 * ```tsx
 * // ✅ CORRECT - Lazy load at consumption site
 * import { loadable } from '@shared/core/loadableFromNamedExport';
 *
 * const Command = loadable(
 *   () => import('@withjoy/joykit/components/Command'),
 *   'Command'
 * );
 *
 * // ❌ INCORRECT - Synchronous import pulls into initial bundle
 * import { Command } from '@withjoy/joykit';
 * ```
 *
 * ## Usage
 *
 * Use as a compound component with sub-components:
 * - Command.Input - Search input field
 * - Command.List - Container for items
 * - Command.Empty - Shown when no results
 * - Command.Group - Groups related items
 * - Command.Item - Selectable item
 * - Command.Separator - Visual divider
 * - Command.Shortcut - Keyboard shortcut display
 * - Command.Dialog - Modal wrapper
 *
 * @example
 * ```tsx
 * <Command>
 *   <Command.Input placeholder="Search..." />
 *   <Command.List>
 *     <Command.Empty>No results found.</Command.Empty>
 *     <Command.Group heading="Suggestions">
 *       <Command.Item onSelect={() => console.log('Selected')}>
 *         <CalendarIcon />
 *         <span>Calendar</span>
 *       </Command.Item>
 *     </Command.Group>
 *   </Command.List>
 * </Command>
 * ```
 */
const CommandRoot: React.FC<CommandProps> = ({ children, overrides = EMPTY_OVERRIDES, className, label = 'Command menu', ...restProps }) => {
  const { contextValue, navigateItems, selectFirst, selectLast, getSortedVisibleItems } = useCommandState(restProps, overrides);
  const [Root, rawRootProps] = getOverrides(overrides.Root, StyledCommandRoot);
  // Extract onKeyDown so it composes with (rather than replaces) the built-in keyboard navigation handler.
  const { onKeyDown: overrideOnKeyDown, ...restRootProps } = rawRootProps as typeof rawRootProps & {
    onKeyDown?: (e: React.KeyboardEvent) => void;
  };
  const rootRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Run override handler first (e.g., dialog Escape handling)
      overrideOnKeyDown?.(e);
      if (e.defaultPrevented) return;

      // Ignore if composing (IME)
      if (e.nativeEvent.isComposing || e.keyCode === 229) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
        case 'n':
          if (e.key === 'n' && !e.ctrlKey) break;
          e.preventDefault();
          if (e.metaKey) {
            selectLast();
          } else {
            navigateItems(1);
          }
          break;

        case 'ArrowUp':
        case 'p':
          if (e.key === 'p' && !e.ctrlKey) break;
          e.preventDefault();
          if (e.metaKey) {
            selectFirst();
          } else {
            navigateItems(-1);
          }
          break;

        case 'Home':
          e.preventDefault();
          selectFirst();
          break;

        case 'End':
          e.preventDefault();
          selectLast();
          break;

        case 'Enter': {
          e.preventDefault();
          // Find the selected item and trigger its onSelect via context callback
          const visibleItems = getSortedVisibleItems();
          const selectedItem = visibleItems.find(item => item.value === contextValue.selectedValue);
          if (selectedItem) {
            contextValue.triggerItemSelect(selectedItem.value);
          }
          break;
        }
      }
    },
    [overrideOnKeyDown, contextValue.selectedValue, navigateItems, selectFirst, selectLast, getSortedVisibleItems],
  );

  return (
    <CommandContext.Provider value={contextValue}>
      <Root
        ref={rootRef}
        __css={commandRootStyles}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={className}
        role="application"
        aria-label={label}
        {...restRootProps}
      >
        {/* Screen reader label */}
        <span
          id={contextValue.labelId}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            borderWidth: 0,
          }}
        >
          {label}
        </span>
        {children}
      </Root>
    </CommandContext.Provider>
  );
};

CommandRoot.displayName = createDisplayName('Command');

// ============================================================================
// CommandDialog Component
// ============================================================================

/**
 * CommandDialog wraps Command in a Modal for use as a command palette overlay.
 *
 * @example
 * ```tsx
 * <Command.Dialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <Command.Input placeholder="Type a command..." />
 *   <Command.List>
 *     <Command.Item>Item 1</Command.Item>
 *   </Command.List>
 * </Command.Dialog>
 * ```
 */
const CommandDialog: React.FC<CommandDialogProps> = ({ isOpen, onClose, children, overrides = {}, shouldFilter, loop, filter, value, onValueChange, label }) => {
  const [Root, rootProps] = getOverrides(overrides.Root, StyledCommandRoot);

  // Handle Escape key to close dialog
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      disableCloseOnOutsideClick={false}
      overrides={{
        // Override Modal.Content to use our desired width and styling
        Content: {
          props: {
            maxWidth: '500px',
            borderRadius: '8px',
          },
        },
      }}
    >
      <Modal.Backdrop />
      <Modal.Content>
        <CommandRoot
          shouldFilter={shouldFilter}
          loop={loop}
          filter={filter}
          value={value}
          onValueChange={onValueChange}
          label={label}
          overrides={{ Root: { component: Root, props: { ...commandDialogRootStyles, onKeyDown: handleKeyDown, ...rootProps } } }}
        >
          {children}
        </CommandRoot>
      </Modal.Content>
    </Modal>
  );
};

CommandDialog.displayName = createDisplayName('CommandDialog');

// ============================================================================
// CommandInput Component
// ============================================================================

/**
 * CommandInput is the search input field for the command palette.
 * Automatically handles search/filter state.
 */
const CommandInput: React.FC<CommandInputProps> = ({ placeholder, overrides = {}, icon, className, value: controlledValue, onValueChange, ...restProps }) => {
  const ctx = useCommandContext();
  const { setSearch } = ctx;
  const [InputWrapper, inputWrapperProps] = getOverrides(overrides.InputWrapper ?? ctx.overrides?.InputWrapper, StyledCommandInputWrapper);
  const [InputIcon, inputIconProps] = getOverrides(overrides.InputIcon ?? ctx.overrides?.InputIcon, StyledCommandInputIcon);
  const [Input, inputProps] = getOverrides(overrides.Input ?? ctx.overrides?.Input, StyledCommandInput);

  const isControlled = controlledValue !== undefined;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!isControlled) {
        setSearch(newValue);
      }
      onValueChange?.(newValue);
    },
    [setSearch, isControlled, onValueChange],
  );

  // Sync controlled value to context
  useEffect(() => {
    if (isControlled) {
      setSearch(controlledValue);
    }
  }, [isControlled, controlledValue, setSearch]);

  // Look up the selected item's unique accessible ID via context
  const selectedItemId = ctx.selectedValue ? ctx.getItemAccessibleId(ctx.selectedValue) : undefined;

  return (
    <InputWrapper
      __css={commandInputWrapperStyles}
      {...inputWrapperProps}
    >
      <InputIcon
        __css={commandInputIconStyles}
        {...inputIconProps}
      >
        {icon ?? <Search size={16} />}
      </InputIcon>
      {
        // @ts-expect-error - Polymorphic as="input" causes type conflict between Box.size and HTMLInputElement.size
        <Input
          as="input"
          __css={commandInputStyles}
          type="text"
          placeholder={placeholder}
          className={className}
          value={isControlled ? controlledValue : ctx.search}
          onChange={handleChange}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-autocomplete="list"
          role="combobox"
          aria-expanded={true}
          aria-controls={ctx.listId}
          aria-labelledby={ctx.labelId}
          aria-activedescendant={selectedItemId}
          id={ctx.inputId}
          {...inputProps}
          {...restProps}
        />
      }
    </InputWrapper>
  );
};

CommandInput.displayName = createDisplayName('CommandInput');

// ============================================================================
// CommandList Component
// ============================================================================

/**
 * CommandList is the container for command items.
 * Handles scrolling and accessibility.
 */
const CommandList: React.FC<CommandListProps> = ({ children, overrides = {}, className, label = 'Suggestions' }) => {
  const ctx = useCommandContext();
  const [List, listProps] = getOverrides(overrides.List ?? ctx.overrides?.List, StyledCommandList);
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (ctx.selectedValue && listRef.current) {
      const selectedElement = listRef.current.querySelector('[aria-selected="true"]');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [ctx.selectedValue]);

  // Look up the selected item's unique accessible ID via context
  const selectedItemId = ctx.selectedValue ? ctx.getItemAccessibleId(ctx.selectedValue) : undefined;

  return (
    <List
      ref={listRef}
      __css={commandListStyles}
      role="listbox"
      tabIndex={-1}
      aria-label={label}
      aria-activedescendant={selectedItemId}
      id={ctx.listId}
      className={className}
      {...listProps}
    >
      {children}
    </List>
  );
};

CommandList.displayName = createDisplayName('CommandList');

// ============================================================================
// CommandEmpty Component
// ============================================================================

/**
 * CommandEmpty is displayed when no items match the search query.
 */
const CommandEmpty: React.FC<CommandEmptyProps> = ({ children, overrides = {}, className }) => {
  const ctx = useCommandContext();
  const [Empty, emptyProps] = getOverrides(overrides.Empty ?? ctx.overrides?.Empty, StyledCommandEmpty);

  // Only show when there are no filtered results
  const filteredCount = ctx.getFilteredCount();
  if (filteredCount > 0) {
    return null;
  }

  return (
    <Empty
      __css={commandEmptyStyles}
      role="presentation"
      className={className}
      {...emptyProps}
    >
      {children}
    </Empty>
  );
};

CommandEmpty.displayName = createDisplayName('CommandEmpty');

// ============================================================================
// CommandGroup Component
// ============================================================================

/**
 * CommandGroup groups related command items together.
 * Items are filtered as a group.
 */
const CommandGroup: React.FC<CommandGroupProps> = ({ heading, children, overrides = {}, className, forceMount, value }) => {
  const ctx = useCommandContext();
  const [Group, groupProps] = getOverrides(overrides.Group ?? ctx.overrides?.Group, StyledCommandGroup);
  const [GroupHeading, groupHeadingProps] = getOverrides(overrides.GroupHeading ?? ctx.overrides?.GroupHeading, StyledCommandGroupHeading);
  const groupId = useId(undefined, 'command-group');
  const headingId = useId(undefined, 'command-heading');

  // Store context functions in ref to avoid effect re-runs when context value changes
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // Register group - only depends on group-specific values
  useEffect(() => {
    const groupValue = value ?? (typeof heading === 'string' ? heading : groupId);
    ctxRef.current.registerGroup(groupId, groupValue);
    return () => ctxRef.current.unregisterGroup(groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, value, heading]);

  // Create group context
  const groupContext: CommandGroupContextValue = useMemo(
    () => ({
      id: groupId,
      forceMount,
    }),
    [groupId, forceMount],
  );

  // Check if group should be visible — hidden via CSS instead of unmounting
  // to keep child items registered and avoid remount delay when search is cleared
  const isVisible = forceMount || !ctx.shouldFilter || !ctx.search || ctx.isGroupVisible(groupId);

  return (
    <CommandGroupContext.Provider value={groupContext}>
      <Group
        __css={{ ...commandGroupStyles, ...(!isVisible && { display: 'none' }) }}
        role="presentation"
        className={className}
        {...groupProps}
      >
        {heading && (
          <GroupHeading
            __css={commandGroupHeadingStyles}
            aria-hidden={true}
            id={headingId}
            {...groupHeadingProps}
          >
            {heading}
          </GroupHeading>
        )}
        <div
          role="group"
          aria-labelledby={heading ? headingId : undefined}
        >
          {children}
        </div>
      </Group>
    </CommandGroupContext.Provider>
  );
};

CommandGroup.displayName = createDisplayName('CommandGroup');

// ============================================================================
// CommandItem Component
// ============================================================================

/**
 * CommandItem is an individual selectable item in the command palette.
 * Supports keyboard navigation and selection.
 */
const CommandItem: React.FC<CommandItemProps> = ({ disabled = false, onSelect, value, keywords = [], children, overrides = {}, className, forceMount }) => {
  const ctx = useCommandContext();
  const groupCtx = useCommandGroupContext();
  const [Item, itemProps] = getOverrides(overrides.Item ?? ctx.overrides?.Item, StyledCommandItem);
  const itemRef = useRef<HTMLDivElement>(null);
  const itemId = useId(undefined, 'command-item');
  const hasRegisteredRef = useRef(false);

  // Extract text content for value if not provided
  const itemValue = value ?? getTextContent(children);

  // Generate stable ID for accessibility using the unique itemId (not the value)
  // to avoid collisions when values normalize identically (e.g., "New Item" vs "new-item")
  const accessibleId = getCommandItemAccessibleId(itemId);

  // Store context functions in ref to avoid effect re-runs when context value changes
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // Register/unregister item - only depends on item-specific values, not ctx
  useEffect(() => {
    ctxRef.current.registerItem(itemId, itemValue, keywords, disabled, groupCtx?.id);
    hasRegisteredRef.current = true;
    return () => ctxRef.current.unregisterItem(itemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, itemValue, keywords, disabled, groupCtx?.id, groupCtx?.forceMount, forceMount]);

  // Update item when props change - only depends on item-specific values
  useEffect(() => {
    ctxRef.current.updateItem(itemId, itemValue, keywords, disabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, itemValue, keywords, disabled]);

  // Handle selection
  const handleSelect = useCallback(() => {
    if (!disabled) {
      ctx.selectItem(itemValue);
      onSelect?.(itemValue);
    }
  }, [ctx, disabled, itemValue, onSelect]);

  // Handle pointer selection
  const handlePointerMove = useCallback(() => {
    if (!disabled) {
      ctx.setSelectedValue(itemValue);
    }
  }, [ctx, disabled, itemValue]);

  // Register callback for keyboard Enter selection via context
  useEffect(() => {
    ctxRef.current.registerItemCallback(itemValue, handleSelect);
    return () => ctxRef.current.unregisterItemCallback(itemValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemValue, handleSelect]);

  // Check visibility — remove from DOM when filtered out (better for accessibility)
  // Only check visibility after initial registration to avoid race condition
  const isVisible = forceMount || groupCtx?.forceMount || !ctx.shouldFilter || !hasRegisteredRef.current || ctx.isItemVisible(itemId);

  if (!isVisible) {
    return null;
  }

  const isSelected = ctx.selectedValue === itemValue;

  return (
    <Item
      ref={itemRef}
      __css={{
        ...commandItemStyles,
        ...(isSelected ? commandItemSelectedStyles : {}),
        ...(disabled ? commandItemDisabledStyles : {}),
      }}
      role="option"
      id={accessibleId}
      aria-disabled={disabled || undefined}
      aria-selected={isSelected}
      data-disabled={disabled || undefined}
      data-selected={isSelected || undefined}
      data-value={itemValue}
      onPointerMove={disabled ? undefined : handlePointerMove}
      onClick={disabled ? undefined : handleSelect}
      className={className}
      {...itemProps}
    >
      {children}
    </Item>
  );
};

CommandItem.displayName = createDisplayName('CommandItem');

// ============================================================================
// CommandSeparator Component
// ============================================================================

/**
 * CommandSeparator is a visual divider between groups.
 */
const CommandSeparator: React.FC<CommandSeparatorProps> = ({ overrides = {}, className, alwaysRender }) => {
  const ctx = useCommandContext();
  const [Separator, separatorProps] = getOverrides(overrides.Separator ?? ctx.overrides?.Separator, StyledCommandSeparator);

  // Hide separator when filtering (unless alwaysRender is true)
  if (!alwaysRender && ctx.search) {
    return null;
  }

  return (
    <Separator
      __css={commandSeparatorStyles}
      role="separator"
      className={className}
      {...separatorProps}
    />
  );
};

CommandSeparator.displayName = createDisplayName('CommandSeparator');

// ============================================================================
// CommandShortcut Component
// ============================================================================

/**
 * CommandShortcut displays keyboard shortcuts for command items.
 */
const CommandShortcut: React.FC<CommandShortcutProps> = ({ children, overrides = {}, className }) => {
  // CommandShortcut doesn't use context overrides since it's rarely styled independently
  const [Shortcut, shortcutProps] = getOverrides(overrides.Shortcut, StyledCommandShortcut);

  return (
    // @ts-expect-error - Polymorphic as="span" causes type conflict between Box.color and HTMLSpanElement.color
    <Shortcut
      as="span"
      __css={commandShortcutStyles}
      className={className}
      {...shortcutProps}
    >
      {children}
    </Shortcut>
  );
};

CommandShortcut.displayName = createDisplayName('CommandShortcut');

// ============================================================================
// Compound Component Assembly
// ============================================================================

type CommandCompound = React.FC<CommandProps> & {
  Dialog: typeof CommandDialog;
  Input: typeof CommandInput;
  List: typeof CommandList;
  Empty: typeof CommandEmpty;
  Group: typeof CommandGroup;
  Item: typeof CommandItem;
  Separator: typeof CommandSeparator;
  Shortcut: typeof CommandShortcut;
};

const Command = CommandRoot as CommandCompound;
Command.Dialog = CommandDialog;
Command.Input = CommandInput;
Command.List = CommandList;
Command.Empty = CommandEmpty;
Command.Group = CommandGroup;
Command.Item = CommandItem;
Command.Separator = CommandSeparator;
Command.Shortcut = CommandShortcut;

export { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator, CommandShortcut };
